import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { cod, drivers } from '@/lib/api';
import { toast } from 'sonner';
import {
  Truck, DollarSign, CheckCircle, Clock, AlertTriangle,
  Plus, X, FileText, ArrowRight, Wallet, ChevronDown,
  ChevronUp, RefreshCw, Banknote, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const colors = {
    OPEN: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    CLOSED: 'bg-blue-100 text-blue-700 border-blue-300',
    CONFIRMED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  };
  const labels = {
    OPEN: 'Aberto',
    CLOSED: 'Fechado',
    CONFIRMED: 'Confirmado',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${colors[status] || 'bg-slate-100 text-slate-600'}`}>
      {labels[status] || status}
    </span>
  );
}

function SettlementCard({ settlement, onClose, onConfirmRepass, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showRepassDialog, setShowRepassDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [repassAmount, setRepassAmount] = useState(settlement.total_to_receive_from_driver || 0);
  const [repassMethod, setRepassMethod] = useState('PIX');
  const [loading, setLoading] = useState(false);

  const handleClose = async () => {
    setLoading(true);
    try {
      await onClose(settlement.id, notes);
      setShowCloseDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Erro ao fechar acerto');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRepass = async () => {
    setLoading(true);
    try {
      await onConfirmRepass(settlement.id, {
        amount: parseFloat(repassAmount),
        method: repassMethod,
        proof_url: null
      });
      setShowRepassDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Erro ao confirmar repasse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`settlement-card-${settlement.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Truck className="text-orange-600" size={24} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-lg">{settlement.driver_name}</h3>
              <p className="text-sm text-slate-500">
                {formatDate(settlement.period_start)} - {formatDate(settlement.period_end)}
              </p>
            </div>
          </div>
          <StatusBadge status={settlement.status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Entregas</p>
            <p className="text-xl font-bold text-slate-900">{settlement.total_deliveries}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">COD Coletado</p>
            <p className="text-xl font-bold text-emerald-600">{formatCurrency(settlement.total_cod_collected)}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Taxa Entrega</p>
            <p className="text-xl font-bold text-blue-600">{formatCurrency(settlement.total_delivery_fees)}</p>
          </div>
          <div className={`rounded-lg p-3 ${settlement.net_amount >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <p className="text-xs text-slate-500 mb-1">Saldo Líquido</p>
            <p className={`text-xl font-bold ${settlement.net_amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatCurrency(settlement.net_amount)}
            </p>
          </div>
        </div>

        {settlement.repass_confirmed && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-emerald-600" size={18} />
              <span className="text-sm font-medium text-emerald-700">
                Repasse confirmado: {formatCurrency(settlement.repass_amount)} via {settlement.repass_method}
              </span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            data-testid={`toggle-details-${settlement.id}`}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
          </button>

          <div className="flex gap-2">
            {settlement.status === 'OPEN' && (
              <Button
                onClick={() => setShowCloseDialog(true)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid={`close-settlement-${settlement.id}`}
              >
                <Clock size={16} className="mr-1" />
                Fechar Acerto
              </Button>
            )}
            {settlement.status === 'CLOSED' && !settlement.repass_confirmed && (
              <Button
                onClick={() => setShowRepassDialog(true)}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`confirm-repass-${settlement.id}`}
              >
                <CheckCircle size={16} className="mr-1" />
                Confirmar Repasse
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <h4 className="font-semibold text-slate-700 mb-3">Detalhamento</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">COD Esperado:</span>
                <span className="font-medium">{formatCurrency(settlement.total_cod_expected)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">COD Coletado:</span>
                <span className="font-medium text-emerald-600">{formatCurrency(settlement.total_cod_collected)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Diferença COD:</span>
                <span className={`font-medium ${settlement.total_cod_collected >= settlement.total_cod_expected ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(settlement.total_cod_collected - settlement.total_cod_expected)}
                </span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between">
                <span className="text-slate-600">A Receber do Motorista:</span>
                <span className="font-medium text-emerald-600">{formatCurrency(settlement.total_to_receive_from_driver)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">A Pagar ao Motorista:</span>
                <span className="font-medium text-red-600">{formatCurrency(settlement.total_to_pay_driver)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Close Settlement Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fechar Acerto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Ao fechar o acerto, as contas financeiras serão geradas automaticamente.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Total COD Coletado:</span>
                <span className="font-semibold text-emerald-600">{formatCurrency(settlement.total_cod_collected)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Taxa de Entrega:</span>
                <span className="font-semibold text-blue-600">{formatCurrency(settlement.total_delivery_fees)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t pt-2">
                <span>Saldo Líquido:</span>
                <span className={settlement.net_amount >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                  {formatCurrency(settlement.net_amount)}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Observações</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observações opcionais..."
                data-testid="close-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>Cancelar</Button>
            <Button onClick={handleClose} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Fechando...' : 'Fechar Acerto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Repass Dialog */}
      <Dialog open={showRepassDialog} onOpenChange={setShowRepassDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Repasse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Confirme o recebimento do valor do motorista para finalizar o acerto.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Valor do Repasse</label>
              <Input
                type="number"
                step="0.01"
                value={repassAmount}
                onChange={(e) => setRepassAmount(e.target.value)}
                data-testid="repass-amount-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Método</label>
              <Select value={repassMethod} onValueChange={setRepassMethod}>
                <SelectTrigger data-testid="repass-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                  <SelectItem value="TRANSFERENCIA">Transferência</SelectItem>
                  <SelectItem value="DEPOSITO">Depósito</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepassDialog(false)}>Cancelar</Button>
            <Button onClick={handleConfirmRepass} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? 'Confirmando...' : 'Confirmar Repasse'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DriverSettlementPage() {
  const [settlements, setSettlements] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creating, setCreating] = useState(false);
  // Period selection - default to last 30 days
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().split('T')[0]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const [settlementsRes, driversRes] = await Promise.all([
        cod.settlements.list(params),
        drivers.list()
      ]);
      setSettlements(settlementsRes.data || []);
      setDriversList(driversRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateSettlement = async () => {
    if (!selectedDriver) {
      toast.error('Selecione um motorista');
      return;
    }

    setCreating(true);
    try {
      // Pass period dates to include deliveries from selected range
      const startDate = periodStart ? `${periodStart}T00:00:00` : null;
      const endDate = periodEnd ? `${periodEnd}T23:59:59` : null;
      await cod.settlements.create(selectedDriver, startDate, endDate);
      toast.success('Acerto criado com sucesso!');
      setShowCreateDialog(false);
      setSelectedDriver('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar acerto');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseSettlement = async (settlementId, notes) => {
    await cod.settlements.close(settlementId, notes);
    toast.success('Acerto fechado com sucesso!');
  };

  const handleConfirmRepass = async (settlementId, data) => {
    await cod.settlements.confirmRepass(settlementId, data);
    toast.success('Repasse confirmado com sucesso!');
  };

  // Summary stats
  const openCount = settlements.filter(s => s.status === 'OPEN').length;
  const closedCount = settlements.filter(s => s.status === 'CLOSED').length;
  const confirmedCount = settlements.filter(s => s.status === 'CONFIRMED').length;
  const totalPending = settlements
    .filter(s => s.status !== 'CONFIRMED')
    .reduce((sum, s) => sum + (s.total_to_receive_from_driver || 0), 0);

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="driver-settlement-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Acerto de Entregadores
            </h1>
            <p className="text-slate-600">Gestão de repasses COD e taxas de entrega</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadData}
              disabled={loading}
              data-testid="refresh-settlements-btn"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#FF6B6B] hover:bg-[#FF7A5A]"
              data-testid="create-settlement-btn"
            >
              <Plus size={18} className="mr-2" />
              Novo Acerto
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="text-yellow-600" size={20} />
              <span className="text-sm font-medium text-slate-600">Abertos</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">{openCount}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="text-blue-600" size={20} />
              <span className="text-sm font-medium text-slate-600">Fechados</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{closedCount}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-emerald-600" size={20} />
              <span className="text-sm font-medium text-slate-600">Confirmados</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{confirmedCount}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="text-orange-600" size={20} />
              <span className="text-sm font-medium text-slate-600">Pendente</span>
            </div>
            <p className="text-2xl font-bold text-orange-700">{formatCurrency(totalPending)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Button
            variant={statusFilter === '' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('')}
            size="sm"
            data-testid="filter-all"
          >
            Todos
          </Button>
          <Button
            variant={statusFilter === 'OPEN' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('OPEN')}
            size="sm"
            data-testid="filter-open"
          >
            Abertos
          </Button>
          <Button
            variant={statusFilter === 'CLOSED' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('CLOSED')}
            size="sm"
            data-testid="filter-closed"
          >
            Fechados
          </Button>
          <Button
            variant={statusFilter === 'CONFIRMED' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('CONFIRMED')}
            size="sm"
            data-testid="filter-confirmed"
          >
            Confirmados
          </Button>
        </div>

        {/* Settlements List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-500">Carregando...</div>
          </div>
        ) : settlements.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Truck className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum acerto encontrado</h3>
            <p className="text-slate-500 mb-4">Crie um novo acerto para começar a gerenciar os repasses dos entregadores.</p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#FF6B6B] hover:bg-[#FF7A5A]"
            >
              <Plus size={18} className="mr-2" />
              Criar Primeiro Acerto
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {settlements.map((settlement) => (
              <SettlementCard
                key={settlement.id}
                settlement={settlement}
                onClose={handleCloseSettlement}
                onConfirmRepass={handleConfirmRepass}
                onRefresh={loadData}
              />
            ))}
          </div>
        )}

        {/* Create Settlement Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo Acerto de Entregador</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Selecione o motorista para criar um novo acerto. O sistema consolidará todas as entregas COD finalizadas do período.
              </p>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Motorista</label>
                <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                  <SelectTrigger data-testid="driver-select">
                    <SelectValue placeholder="Selecione um motorista" />
                  </SelectTrigger>
                  <SelectContent>
                    {driversList.map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Data Início</label>
                  <Input
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    data-testid="period-start-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Data Fim</label>
                  <Input
                    type="date"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    data-testid="period-end-input"
                  />
                </div>
              </div>
              {driversList.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700">
                    <AlertTriangle size={16} className="inline mr-1" />
                    Nenhum motorista cadastrado. Cadastre um motorista primeiro.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
              <Button
                onClick={handleCreateSettlement}
                disabled={creating || !selectedDriver}
                className="bg-[#FF6B6B] hover:bg-[#FF7A5A]"
                data-testid="confirm-create-settlement-btn"
              >
                {creating ? 'Criando...' : 'Criar Acerto'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}

export default DriverSettlementPage;
